import { useQuery } from "@apollo/client";
import { withErrorBoundary } from "@sentry/react";
import { keyBy } from "lodash";
import moment from "moment";
import { GoalArtifactNode, MeetingNode } from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import GoalProgress from "@apps/goal-progress/goal-progress";
import { GoalProgressView } from "@apps/goal-progress/goal-progress-constants";
import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar, currentUserVar } from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Error from "@components/error/error";
import Layout from "@components/layout/layout";
import AppLink from "@components/link/link";
import { assertEdgesNonNull, getUrl } from "@helpers/helpers";

import getHomepageDirectReportsMetadataQuery from "../graphql/get-homepage-direct-reports-metadata-query";
import { usePollForCalendarInsights } from "./use-poll-for-calendar-insights";

const directReportGoalMax = 3;

type DirectReportGoal = Pick<
  GoalArtifactNode,
  | "currentValue"
  | "id"
  | "isStale"
  | "progress"
  | "progressType"
  | "startValue"
  | "state"
  | "status"
  | "targetValue"
  | "title"
> & {
  readonly __typename: "GoalArtifactNode";
  readonly canUpdate: {
    readonly permission: boolean;
  };
};

type DirectReportMeeting = Pick<
  MeetingNode,
  "id" | "meetingGroupId" | "startDatetime"
>;

type DirectReportMetadataFromQuery = {
  readonly id: number;
  readonly firstName?: string | null;
  readonly name: string;
  readonly avatar?: string | null;
  readonly email?: string | null;
  readonly calendarInsightStatus?: string | null;
  readonly homepageStatus?: string | null;
  readonly goals?: {
    readonly totalCount: number;
    readonly edges: readonly ({
      readonly node?:
        | DirectReportGoal
        | { readonly id: number; readonly __typename?: string }
        | null;
    } | null)[];
  } | null;
  readonly meetings?: {
    readonly edges: readonly ({
      readonly node?: DirectReportMeeting | null;
    } | null)[];
  } | null;
};

type DirectReportMetadata = Omit<DirectReportMetadataFromQuery, "meetings"> & {
  meetings: DirectReportMeeting[];
};

type HomepageDirectReportsMetadataQuery = {
  readonly directReports?: {
    readonly edges: readonly ({
      readonly node?: DirectReportMetadataFromQuery | null;
    } | null)[];
  };
};

type HomepageDirectReportsMetadataQueryVariables = {
  organizationId: number;
  currentUserId: number;
  directReportIds: number[];
  goalMax: number;
  hasDirectReportIds: boolean;
  now: string;
};

const formatMeetingDate = (startDatetime?: string | null) =>
  startDatetime
    ? moment(startDatetime).format("ddd @ h:mma").replace(":00", "")
    : null;

// Strip markdown and collapse whitespace. The status is a short one-liner, so it is rendered in
// full with no truncation (TF-1500). Handles Slack mrkdwn links (<url|label>) as well as
// CommonMark links, and leaves literal hyphens intact so "1-on-1" survives.
const stripSummaryMarkdown = (summary?: string | null) =>
  (summary || "")
    .replace(/<[^|>]+\|([^>]+)>/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/[`*_>#~]/g, "")
    .replace(/\s+/g, " ")
    .trim();

export const DirectReportSummary = ({
  homepageStatus,
  calendarInsightFallback,
}: {
  homepageStatus?: string | null;
  calendarInsightFallback?: string | null;
}) => {
  // Prefer the work-history homepage status; fall back to the cached calendar insight (TF-1429).
  const summary =
    stripSummaryMarkdown(homepageStatus) ||
    stripSummaryMarkdown(calendarInsightFallback);

  return (
    <p className="text-sm leading-5 text-gray-600">
      {summary || "No recent updates."}
    </p>
  );
};

const DirectReportCard = ({
  directReport,
  metadata,
  loadingMetadata,
}: {
  directReport: BasicUser;
  metadata?: DirectReportMetadata;
  loadingMetadata: boolean;
}) => {
  const label = useLabel();
  const organization = currentOrganizationVar();
  const goals = metadata?.goals ? assertEdgesNonNull(metadata.goals) : [];
  const openGoals = goals.filter(
    (goal): goal is DirectReportGoal => goal.__typename === "GoalArtifactNode",
  );
  const remainingGoalCount =
    (metadata?.goals?.totalCount || openGoals.length) - openGoals.length;
  const nextMeeting = metadata?.meetings?.[0];
  const nextMeetingDate = formatMeetingDate(nextMeeting?.startDatetime);

  return (
    <div className="rounded-md border border-gray-200 bg-white px-4 py-3 min-h-[108px]">
      <div className="flex gap-3">
        <AppLink to={getUrl({ userId: directReport.id })} className="shrink-0">
          <Avatar user={directReport} size="8" showTooltip={false} />
        </AppLink>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex items-center gap-2">
              <AppLink
                to={getUrl({ userId: directReport.id })}
                className="truncate text-sm font-medium text-gray-900 hover:underline"
              >
                {directReport.name}
              </AppLink>
              {organization.featureFlags.goals && (
                <div className="flex shrink-0 items-center gap-1">
                  {loadingMetadata ? (
                    <div className="skeleton h-3 w-12 rounded" />
                  ) : (
                    <>
                      {openGoals.map((goal) => (
                        <GoalProgress
                          key={goal.id}
                          artifact={goal}
                          withPopover
                          goalProgressView={GoalProgressView.pillOnly}
                        />
                      ))}
                      {remainingGoalCount > 0 && (
                        <AppLink
                          to={`/goals/user/${directReport.id}`}
                          className="text-xs font-medium text-gray-500 hover:underline"
                        >
                          +{remainingGoalCount}
                        </AppLink>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
            {organization.featureFlags.meetings && (
              <div className="shrink-0 text-right text-xs font-medium">
                {loadingMetadata ? (
                  <div className="skeleton h-4 w-16 rounded" />
                ) : nextMeeting && nextMeetingDate ? (
                  <AppLink
                    to={getUrl({
                      meetingGroupId: nextMeeting.meetingGroupId,
                      meetingId: nextMeeting.id,
                    })}
                    className="text-gray-500 hover:underline"
                  >
                    {nextMeetingDate}
                  </AppLink>
                ) : (
                  <span className="text-amber-600">
                    No {label("oneonone")} set
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="mt-1">
            {loadingMetadata && !metadata ? (
              <div className="skeleton h-4 w-full rounded" />
            ) : (
              <DirectReportSummary
                homepageStatus={metadata?.homepageStatus}
                calendarInsightFallback={
                  organization.featureFlags.workHistory
                    ? metadata?.calendarInsightStatus
                    : null
                }
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const DirectReportsComponent = () => {
  const currentUser = currentUserVar();
  const organization = currentOrganizationVar();
  const directReports = currentUser.directReports
    ? assertEdgesNonNull(currentUser.directReports)
    : [];
  const directReportIds = directReports.map((directReport) => directReport.id);
  const { data, loading, refetch } = useQuery<
    HomepageDirectReportsMetadataQuery,
    HomepageDirectReportsMetadataQueryVariables
  >(getHomepageDirectReportsMetadataQuery, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "cache-and-network",
    skip: directReportIds.length === 0,
    variables: {
      organizationId: organization.id,
      directReportIds,
      hasDirectReportIds: directReportIds.length > 0,
      goalMax: directReportGoalMax,
      currentUserId: currentUser.id,
      now: moment().startOf("hour").format(),
    },
  });

  const usersWithMetadata = data?.directReports
    ? assertEdgesNonNull(data.directReports).map((user) => ({
        ...user,
        meetings: user.meetings ? assertEdgesNonNull(user.meetings) : [],
      }))
    : [];

  // Number of reports whose calendar insight has been generated so far.
  const populatedInsightCount = usersWithMetadata.filter(
    (user) => (user.calendarInsightStatus ?? "").trim().length > 0,
  ).length;

  usePollForCalendarInsights({
    populatedInsightCount,
    enabled:
      directReportIds.length > 0 && !!organization.featureFlags.workHistory,
    refetch,
  });

  if (directReports.length === 0) {
    return null;
  }

  const metadataByUserId = keyBy(usersWithMetadata, "id");
  const reportsToRender = usersWithMetadata.map((user) => ({
    id: user.id,
    firstName: user.firstName || undefined,
    name: user.name,
    avatar: user.avatar,
    email: user.email || undefined,
  }));

  if (loading && reportsToRender.length === 0) {
    return (
      <Layout.MainSubSection title="Your Direct Reports">
        <div className="grid grid-cols-1 gap-3 @[640px]/layout-main:grid-cols-2">
          {directReports.map((directReport) => (
            <div
              key={directReport.id}
              className="rounded-md border border-gray-200 bg-white px-4 py-3 min-h-[108px]"
            >
              <div className="flex gap-3">
                <div className="skeleton h-8 w-8 shrink-0 rounded-full" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="skeleton h-4 w-32 rounded" />
                    <div className="skeleton h-4 w-16 rounded" />
                  </div>
                  <div className="mt-3 skeleton h-4 w-full rounded" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </Layout.MainSubSection>
    );
  }

  if (reportsToRender.length === 0) {
    return null;
  }

  return (
    <Layout.MainSubSection title="Your Direct Reports">
      <div className="grid grid-cols-1 gap-3 @[640px]/layout-main:grid-cols-2">
        {reportsToRender.map((directReport) => (
          <DirectReportCard
            key={directReport.id}
            directReport={directReport}
            metadata={metadataByUserId[directReport.id]}
            loadingMetadata={loading && !data}
          />
        ))}
      </div>
    </Layout.MainSubSection>
  );
};

const DirectReports = withErrorBoundary(DirectReportsComponent, {
  fallback: <Error description="Your direct reports could not be rendered." />,
});

export default DirectReports;
